import type { Language, Locales } from '@personly/libs-types';

const makeLocalizedPathname = (locales: Locales) => (pathname: string) => {
  const localeMatcher = new RegExp(`^/(${locales.locales.join('|')})`);

  if (locales.locale === locales.defaultLocale) {
    const hasLocale = localeMatcher.exec(pathname);
    if (!hasLocale) return pathname;

    const nonLocaleMatcher = new RegExp(`^/${hasLocale[1]}/(.*)`);
    const nonLocale = nonLocaleMatcher.exec(pathname);
    return !nonLocale ? `/` : `/${nonLocale[1]}`;
  }

  const hasLocale = localeMatcher.exec(pathname);
  if (hasLocale) {
    const nonLocaleMatcher = new RegExp(`^/${hasLocale[1]}/(.*)`);
    const nonLocale = nonLocaleMatcher.exec(pathname);
    return !nonLocale
      ? `/${locales.locale}`
      : `/${locales.locale}/${nonLocale[1]}`;
  }

  return pathname === '/'
    ? `/${locales.locale}`
    : `/${locales.locale}${pathname}`;
};

const toPathname = (slug: string) => `/${slug}`;

const toRelativePathname = (...entries: string[]) =>
  entries.join('/').toLowerCase();

export {
  makeLocalizedPathname,
  stripTrailingSlashes,
  toLocale,
  toLocales,
  toPathname,
  withLocale,
};

const toLocale = (language: Language) => language.id.toLowerCase();

const toLocales = (languages: Language[]): Omit<Locales, 'locale'> => {
  const defaultLanguage = languages.find((language) => language.flags.active);

  const locales = languages.map(toLocale);
  const defaultLocale = toLocale(defaultLanguage!);

  return { locales, defaultLocale };
};

const withLocale = (
  locale: string,
  locales: Omit<Locales, 'locale'>
): Locales => ({
  ...locales,
  locale: locales.locales.includes(locale) ? locale : locales.defaultLocale,
});

const stripTrailingSlashes = (url: URL) => url.href.replace(/\/+$/, '');

export { toRelativePathname };
